import { cloneDeep } from 'lodash';
import {
    QueryKey,
    QueryKeyRuntime,
    QueryFilterPlaceholder,
    QueryFilterPlaceholderHandler,
    QueryFilterOperatorValidator
} from './query-const';
import { DatasetQuerySyntaxError } from './errors';

/* eslint-disable jsdoc/require-returns, jsdoc/require-param-description, spaced-comment */

/**
 * @typedef {import('@goodt-common/data').IDatasetQuery} IDatasetQuery
 * @typedef {import('@goodt-common/data').IDatasetQuerySort} IDatasetQuerySort
 * @typedef {import('@goodt-common/data').IDatasetQueryFilter} IDatasetQueryFilter
 * @typedef {import('@goodt-common/data').IDatasetQueryVariableValue} IDatasetQueryVariableValue
 * @typedef {import('@goodt-common/data').IQueryVariableInfo} IQueryVariableInfo
 * @typedef {import('@goodt-common/data').IQueryFilterInfo} IQueryFilterInfo
 * @typedef {import('@goodt-common/data').IQuerySortInfo} IQuerySortInfo
 * @typedef {import('@goodt-common/data').IDatasetQueryMetricCustom} IDatasetQueryMetricCustom
 * @typedef {import('@goodt-common/data').IQueryMetricCustomInfo} IQueryMetricCustomInfo
 */

/**
 * @param {object} obj
 * @returns {string}
 */
const objectGetFirstKey = (obj) => Object.keys(obj)[0] ?? null;

////////////////////////////////
//
// Filter
//
////////////////////////////////

/**
 * @param {IQueryFilterInfo['value']} value
 * @return {FlatArray<*[], 1>[]}
 */
const filterNormalizeValue = (value) => [value].flat();

/**
 * @param {Omit<IQueryFilterInfo, 'name'>} options
 * @return {boolean}
 */
export const queryFilterIsValidInfo = ({ operator, value }) => {
    const isValid = QueryFilterOperatorValidator[operator];
    if (isValid === undefined) {
        return false;
    }

    return isValid(filterNormalizeValue(value));
};

/**
 * @param {IQueryFilterInfo} options
 * @returns {IDatasetQueryFilter}
 */
export const queryFilterCreate = ({ name, operator, value }) => {
    const isValid = queryFilterIsValidInfo({ operator, value });
    if (isValid) {
        return { [name]: { [operator]: filterNormalizeValue(value) } };
    }
    throw new DatasetQuerySyntaxError(`Dataset query filter is not valid: "${name} ${operator} ${value}"`, {
        data: {
            name,
            operator,
            value
        }
    });
};

/**
 * @param {{ query:.IDatasetQuery, filter: IDatasetQueryFilter|string }} options
 * @returns {number}
 */
export const queryFilterFindIndex = ({ query, filter }) => {
    const filterName = typeof filter === 'string' ? filter : objectGetFirstKey(filter);
    return query[QueryKey.FILTERS].findIndex((filterItem) => objectGetFirstKey(filterItem) === filterName);
};

/**
 * @param {{ query: IDatasetQuery, filter: IDatasetQueryFilter|string }} options
 * @returns {boolean}
 */
export const queryFilterExists = ({ query, filter }) => queryFilterFindIndex({ query, filter }) >= 0;

/**
 * @param {{ query:IDatasetQuery, filter: IDatasetQueryFilter }} options
 * @returns {IDatasetQuery}
 */
export const queryFilterUpdate = ({ query, filter }) => {
    const index = queryFilterFindIndex({ query, filter });
    const queryClone = cloneDeep(query);

    if (index >= 0) {
        queryClone[QueryKey.FILTERS].splice(index, 1, filter);
    }
    return queryClone;
};

/**
 * @param {IDatasetQueryFilter} filter
 * @returns {IQueryFilterInfo}
 */
export const queryFilterGetInfo = (filter) => {
    const name = objectGetFirstKey(filter);
    const definition = filter[name];
    const operator = objectGetFirstKey(definition);
    const value = definition[operator];
    return { name, operator, value };
};

const isPrimitive = (value) => {
    const isNull = String(value).toLowerCase() === 'null';
    const isNumber = value !== '' && !Number.isNaN(Number(String(value))) && !String(value).match(/^(0\d|\+|\.|0x)/i);
    const isBoolean = ['true', 'false'].includes(String(value).toLocaleLowerCase());
    return isNull || isNumber || isBoolean;
};

const isString = (value) => {
    if (typeof value === 'string') {
        return value.match(/^'(.*)'/) !== null;
    }
    return false;
};

const isExpression = (value) => {
    if (typeof value === 'string') {
        // '{{expression}}'
        return value.match(/^\{\{(.+)\}\}$/) !== null;
    }
    return false;
};

const extractExpression = (value) => {
    if (isExpression(value)) {
        // '{{expression}}' -> 'expression'
        return value.replace(/^\{\{(.+)\}\}$/, '$1');
    }
    return null;
};

export const formatFilterValue = (value) => {
    if (isPrimitive(value)) {
        return String(value);
    }
    if (isString(value)) {
        return value;
    }
    if (isExpression(value)) {
        return extractExpression(value);
    }

    return `'${value}'`;
};

/**
 *
 * @param query
 * @return {*}
 */
export const queryFormatFiltersValues = ({ query }) => {
    const queryClone = cloneDeep(query);
    queryClone[QueryKey.FILTERS] = queryClone[QueryKey.FILTERS].map((filterItem) => {
        const filterInfo = queryFilterGetInfo(filterItem);
        filterInfo.value = filterInfo.value.map(formatFilterValue);
        return queryFilterCreate(filterInfo);
    });
    return queryClone;
};

////////////////////////////////
//
// Sort
//
////////////////////////////////

/**
 * @param {IQuerySortInfo} options
 * @returns {IDatasetQuerySort}
 */
export const querySortCreate = ({ name, direction }) => ({ [name]: direction });

/**
 * @param {{ query:IDatasetQuery, sort: IDatasetQuerySort | string }} options
 * @returns {number}
 */
export const sortFindIndex = ({ query, sort }) => {
    const sortName = typeof sort === 'string' ? sort : objectGetFirstKey(sort);
    return query[QueryKey.SORT].findIndex((sortItem) => objectGetFirstKey(sortItem) === sortName);
};

/**
 * @param {{ query:IDatasetQuery, sort: IDatasetQuerySort | string }} options
 * @returns {boolean}
 */
export const querySortExists = ({ query, sort }) => sortFindIndex({ query, sort }) >= 0;

/**
 * @param {{ query: IDatasetQuery, sort: IDatasetQuerySort | string }} options
 * @returns {IDatasetQuery}
 */
export const querySortUpdate = ({ query, sort }) => {
    const index = sortFindIndex({ query, sort });

    if (index >= 0) {
        const queryClone = cloneDeep(query);
        queryClone[QueryKey.SORT].splice(index, 1, sort);
        return queryClone;
    }
    return query;
};

/**
 * @param {IDatasetQuerySort} sort
 * @returns {IQuerySortInfo}
 */
export const querySortGetInfo = (sort) => {
    const name = objectGetFirstKey(sort);
    const direction = sort[name];
    return { name, direction };
};

////////////////////////////////
//
// Variables
//
////////////////////////////////

/**
 * @param {{ query: IDatasetQuery, variable: string | IDatasetQueryVariable }} options
 * @returns {number}
 */
export const queryVariableFindIndex = ({ query, variable }) => {
    const variableName = typeof variable === 'string' ? variable : objectGetFirstKey(variable);
    return query[QueryKey.VARIABLES].findIndex((sortItem) => objectGetFirstKey(sortItem) === variableName);
};

/**
 * @param {{ query:IDatasetQuery, variable: IDatasetQueryVariable | string }} options
 * @returns {boolean}
 */
export const queryVariableExists = ({ query, variable }) => queryVariableFindIndex({ query, variable }) >= 0;

/**
 *
 * @param {IDatasetQueryVariable} queryVariable
 * @return {IQueryVariableInfo}
 */
export const queryVariableGetInfo = (queryVariable) => {
    /**
     * @type {string}
     */
    const variableName = objectGetFirstKey(queryVariable);
    /**
     * @type {IDatasetQueryVariableValue}
     */
    const valueObject = queryVariable[variableName];
    return {
        name: variableName,
        value: valueObject == null ? null : valueObject.value
    };
};

/**
 *
 * @param {Required<IQueryVariableInfo, 'name'>} queryVariableInfo
 * @return {IDatasetQueryVariable}
 */
export const queryVariableCreate = (queryVariableInfo) => {
    const { name, value = null } = queryVariableInfo;
    return {
        // [name]: value == null ? null : { value }
        [name]: { value }
    };
};

////////////////////////////////
//
// Custom Metrics
//
////////////////////////////////

/**
 * @param {IQueryMetricCustomInfo} options
 * @returns {IDatasetQueryMetricCustom}
 */
export const queryMetricCustomCreate = ({ name, func, field }) => ({
    name,
    definition: `${func}("${field}")`
});

/**
 * @param {IDatasetQueryMetricCustom} metricCustom
 * @returns {IQueryMetricCustomInfo}
 */
export const queryMetricCustomGetInfo = (metricCustom) => {
    const { name, definition } = metricCustom;
    const [, func, field] = definition.match(/(\w+)\("(\w+)"\)/);
    return { name, func, field };
};

/**
 * @param {{ query:IDatasetQuery, metric:IDatasetQueryMetricCustom|string }} options
 * @returns {number}
 */
export const queryMetricCustomFindIndex = ({ query, metric }) => {
    const metricName = metric?.name ?? metric;
    return query[QueryKey.METRICS_CUSTOM].findIndex(({ name }) => name === metricName);
};

/**
 * @param {{ query:IDatasetQuery, metric:IDatasetQueryMetricCustom|string }} options
 * @returns {boolean}
 */
export const queryMetricCustomExists = ({ query, metric}) => queryMetricCustomFindIndex({ query, metric }) >= 0;

/**
 * @param {{ query:IDatasetQuery, metric:IDatasetQueryMetricCustom }} options
 * @returns {IDatasetQuery}
 */
export const queryMetricCustomUpdate = ({ query, metric }) => {
    const index = queryMetricCustomFindIndex({ query, metric });
    const queryClone = cloneDeep(query);
    
    if (index >= 0) {
        queryClone[QueryKey.METRICS_CUSTOM].splice(index, 1, metric);
    }
    return queryClone;
};

////////////////////////////////
//
// Query
//
////////////////////////////////

/**
 * @param {Partial<IDatasetQuery>} [options = {}]
 * @return {IDatasetQuery}
 */
export const queryCreate = ({
    [QueryKey.FROM]: $from = '',
    [QueryKey.FIELDS]: $fields = [],
    [QueryKey.METRICS]: $metrics = [],
    [QueryKey.DIMENSIONS]: $dimensions = [],
    [QueryKey.FILTERS]: $filters = [],
    [QueryKey.VARIABLES]: $variables = [],
    [QueryKey.ORDER]: $order = [],
    [QueryKey.SORT]: $sort = [],
    [QueryKey.METRICS_CUSTOM]: $metricsCustom,
    [QueryKeyRuntime.DRILLDOWNS]: $drilldowns = []
} = {}) => ({
    [QueryKey.FROM]: $from,
    [QueryKey.FIELDS]: $fields,
    [QueryKey.METRICS]: $metrics,
    [QueryKey.DIMENSIONS]: $dimensions,
    [QueryKey.FILTERS]: $filters,
    [QueryKey.VARIABLES]: $variables,
    [QueryKey.ORDER]: $order,
    [QueryKey.SORT]: $sort,
    [QueryKeyRuntime.DRILLDOWNS]: $drilldowns,
    ...($metricsCustom != null && {
        [QueryKey.METRICS_CUSTOM]: $metricsCustom
    })
});

/**
 * @param {Pick<IDatasetRequestProps, 'datasetName'> & { query: Partial<IDatasetQuery>}} [options = {}]
 * @returns {IDatasetQuery}
 */
export const queryCreateFromRequestProps = ({ datasetName: $from, query = {} } = {}) =>
    queryCreate({ ...query, ...$from && { $from } });

/**
 * @param {{ query:IDatasetQuery }} options
 * @returns {IDatasetQuery}
 */
export const queryReplaceFilterPlaceholders = ({ query }) => {
    const placeholders = Object.entries(QueryFilterPlaceholder);
    const queryClone = cloneDeep(query);
    queryClone[QueryKey.FILTERS] = queryClone[QueryKey.FILTERS].map((filterItem) => {
        const filterInfo = queryFilterGetInfo(filterItem);
        filterInfo.value = filterInfo.value.map((valueString) =>
            placeholders.reduce((acc, [placeholderKey, placeholderValue]) => {
                const placeholderValueFn = QueryFilterPlaceholderHandler[placeholderKey];
                return acc.replaceAll(placeholderValue, placeholderValueFn ? placeholderValueFn() : placeholderValue);
            }, valueString)
        );
        return queryFilterCreate(filterInfo);
    });
    return queryClone;
};

/**
 * @param {{ query:IDatasetQuery, filter: IDatasetQueryFilter }} options
 * @param {boolean} [forceUpdate=true]
 * @returns {IDatasetQuery}
 */
export const queryFilterAdd = ({ query, filter }, forceUpdate = true) => {
    if (queryFilterExists({ query, filter }) === false) {
        const queryClone = cloneDeep(query);
        queryClone[QueryKey.FILTERS].push(filter);

        return queryClone;
    }

    return forceUpdate ? queryFilterUpdate({ query, filter }) : query;
};

/**
 * @param {{ query:IDatasetQuery, filter: string|IDatasetQueryFilter }} options
 * @returns {IDatasetQuery}
 */
export const queryFilterRemove = ({ query, filter }) => {
    const index = queryFilterFindIndex({ query, filter });
    if (index >= 0) {
        const queryClone = cloneDeep(query);
        queryClone[QueryKey.FILTERS].splice(index, 1);

        return queryClone
    }

    return query;
};

/**
 *
 * @param {{ query:IDatasetQuery }} options
 * @returns {IDatasetQuery}
 */
export const queryFilterRemoveAll = ({ query }) => {
    const queryClone = cloneDeep(query);
    queryClone[QueryKey.FILTERS] = [];

    return queryClone;
};

/**
 * @param {{ query:IDatasetQuery, sort: IDatasetQuerySort }} options
 * @param {boolean} [forceUpdate=true]
 * @returns {IDatasetQuery}
 */
export const querySortAdd = ({ query, sort }, forceUpdate = true) => {
    if (querySortExists({ query, sort }) === false) {
        const queryClone = cloneDeep(query);
        queryClone[QueryKey.SORT].push(sort);

        return queryClone;
    }

    return forceUpdate ? querySortUpdate({ query, sort }) : query;
};

/**
 *
 * @param {{ query:IDatasetQuery, sort: IDatasetQuerySort|string }} options
 * @returns {IDatasetQuery}
 */
export const querySortRemove = ({ query, sort }) => {
    const index = sortFindIndex({ query, sort });
    if (index >= 0) {
        const queryClone = cloneDeep(query);
        queryClone[QueryKey.SORT].splice(index, 1);

        return queryClone;
    }

    return query;
};

/**
 *
 * @param {{ query:IDatasetQuery }} options
 * @returns {IDatasetQuery}
 */
export const querySortRemoveAll = ({ query }) => {
    const queryClone = cloneDeep(query);
    queryClone[QueryKey.SORT] = [];
    return queryClone;
};

/**
 * @param {{ query:IDatasetQuery, variable: IDatasetQueryVariable }} options
 * @returns {IDatasetQuery}
 */
export const queryVariableUpdate = ({ query, variable }) => {
    const index = queryVariableFindIndex({ query, variable });

    if (index >= 0) {
        const queryClone = cloneDeep(query);
        queryClone[QueryKey.VARIABLES].splice(index, 1, variable);
        return queryClone;
    }

    return query;
};

/**
 * @param {{ query:IDatasetQuery, variable: IDatasetQueryVariable }} options
 * @param {boolean} [forceUpdate=true]
 * @returns {IDatasetQuery}
 */
export const queryVariableAdd = ({ query, variable }, forceUpdate = true) => {
    if (queryVariableExists({ query, variable }) === false) {
        const queryClone = cloneDeep(query);
        queryClone[QueryKey.VARIABLES].push(variable);

        return queryClone;
    }

    return forceUpdate ? queryVariableUpdate({ query, variable }) : query;
};

/**
 * @param {{ query:IDatasetQuery, variable: IDatasetQueryVariable | string }} options
 * @returns {IDatasetQuery}
 */
export const queryVariableRemove = ({ query, variable }) => {
    const index = queryVariableFindIndex({ query, variable });
    if (index >= 0) {
        const queryClone = cloneDeep(query);
        queryClone[QueryKey.FILTERS].splice(index, 1);

        return queryClone;
    }

    return query;
};

/**
 * @param {{ query:IDatasetQuery, metric:IDatasetQueryMetricCustom }} options
 * @param {boolean} [forceUpdate=true]
 * @returns {IDatasetQuery}
 */
export const queryMetricCustomAdd = ({ query, metric }, forceUpdate = true) => {
    if (queryMetricCustomExists({ query, metric }) === false) {
        const queryClone = cloneDeep(query);
        queryClone[QueryKey.METRICS_CUSTOM].push(metric);
        
        return queryClone;
    }
    
    return forceUpdate ? queryMetricCustomUpdate({ query, metric }) : query;
};

/**
 * @param {{ query:IDatasetQuery, metric:IDatasetQueryMetricCustom|string }} options
 * @returns {IDatasetQuery}
 */
export const queryMetricCustomRemove = ({ query, metric }) => {
    const index = queryMetricCustomFindIndex({ query, metric });
    if (index >= 0) {
        const queryClone = cloneDeep(query);
        queryClone[QueryKey.METRICS_CUSTOM].splice(index, 1);
        
        return queryClone;
    }
    
    return query;
};

/**
 * @param {{ query:IDatasetQuery }} options
 * @returns {IDatasetQuery}
 */
export const queryMetricCustomRemoveAll = ({ query }) => {
    const queryClone = cloneDeep(query);
    queryClone[QueryKey.METRICS_CUSTOM] = [];
    return queryClone;
};
